<template>
    <UserLayout :page-title="t('dashboard')" :page-section="t('overview')">

        <div class="d-wrap">
            <div class="d-inner">

                <!-- ── WELCOME STRIP ── -->
                <div class="d-welcome anim-up" style="--d:0ms">
                    <div class="dw-left">
                        <div class="dw-av">{{ userInitials }}</div>
                        <div>
                            <p class="dw-greet">{{ getGreeting() }}</p>
                            <p class="dw-name">{{ user.display_name || user.name || 'WalletConnect User' }}</p>
                            <p class="dw-email">{{ user.email }}</p>
                            <div v-if="user.uid" class="dw-uid-row">
                                <span class="dw-uid-lbl">UID</span>
                                <span class="dw-uid-val">{{ user.uid }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="dw-chips">
                        <div class="dw-chip">
                            <span class="dw-chip-l">APY</span>
                            <span class="dw-chip-v" style="color:#a75ded">{{ wallet.currentAPY || '27.8' }}%</span>
                        </div>
                        <div class="dw-chip">
                            <span class="dw-chip-l">Plans</span>
                            <span class="dw-chip-v">{{ activeInvestments.length }}</span>
                        </div>
                        <div class="dw-chip">
                            <span class="dw-chip-l">ROI</span>
                            <span class="dw-chip-v" style="color:#10b981">{{ calculateROI() }}%</span>
                        </div>
                        <div class="dw-bal">
                            <div class="flex items-center gap-2">
                                <span class="dw-chip-l">Main Balance</span>
                                <span :class="wallet.dailyChange>=0?'d-trend-up':'d-trend-dn'">{{ wallet.dailyChange>=0?'▲':'▼' }}{{ Math.abs(wallet.dailyChange) }}%</span>
                            </div>
                            <span class="dw-bal-v">{{ currencySymbol }}{{ formatCurrency(displayBalance) }}</span>
                        </div>
                        <div class="dw-bal" v-if="wallet.hasOwnProperty('rio_balance')">
                            <span class="dw-chip-l">BTC Rewards ₿</span>
                            <span class="dw-bal-v" style="color:#FF6F00">₿{{ formatBtcAmount(displayRioBalance, 12) }}</span>
                        </div>
                    </div>
                </div>

                <!-- ── STAT CARDS ── -->
                <div class="d-stat-row">
                    <!-- Balance -->
                    <div class="d-stat anim-up" style="--d:40ms;--ac:167,93,237">
                        <div class="ds-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(167,93,237,0.2),transparent 70%)"></div>
                        <div class="ds-top">
                            <div class="ds-icon" style="background:rgba(167,93,237,0.15);box-shadow:0 0 12px rgba(167,93,237,0.2)">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#a75ded" stroke-width="2" stroke-linecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                            </div>
                            <span class="ds-lbl">{{ t('portfolioBalance') }}</span>
                        </div>
                        <p class="ds-val">{{ currencySymbol }}{{ formatCurrency(displayBalance) }}</p>
                        <div class="ds-sub" :style="wallet.dailyChange>=0?'color:#10b981':'color:#f43f5e'">
                            {{ wallet.dailyChange>=0?'▲':'▼' }} {{ Math.abs(wallet.dailyChange) }}% <span style="color:#374151">24h</span>
                        </div>
                        <div class="ds-bar-track"><div class="ds-bar shimmer-bar" style="width:72%;background:linear-gradient(90deg,#7c3aed,#a75ded,#c084fc)"></div></div>
                    </div>

                    <!-- Rio Wallet -->
                    <div v-if="wallet.hasOwnProperty('rio_balance')" class="d-stat anim-up" style="--d:60ms;--ac:255,111,0">
                        <div class="ds-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(255,111,0,0.2),transparent 70%)"></div>
                        <div class="ds-top">
                            <div class="ds-icon" style="background:rgba(255,111,0,0.15);box-shadow:0 0 12px rgba(255,111,0,0.2)">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#FF6F00" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 6v6l4 2"/></svg>
                            </div>
                            <span class="ds-lbl">BTC Rewards ₿</span>
                        </div>
                        <p class="ds-val" style="color:#FF6F00">₿{{ formatBtcAmount(displayRioBalance, 12) }}</p>
                        <div class="ds-sub" style="color:#FF6F00">{{ t('investmentProfits') || 'Investment Profits' }}</div>
                        <div class="ds-bar-track"><div class="ds-bar shimmer-bar" style="width:45%;background:linear-gradient(90deg,#ff6f00,#ffb74d,#ffe0b2)"></div></div>
                    </div>

                    <!-- Invested -->
                    <div class="d-stat anim-up" style="--d:80ms;--ac:34,211,238">
                        <div class="ds-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(34,211,238,0.15),transparent 70%)"></div>
                        <div class="ds-top">
                            <div class="ds-icon" style="background:rgba(34,211,238,0.12);box-shadow:0 0 12px rgba(34,211,238,0.18)">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" stroke-width="2" stroke-linecap="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
                            </div>
                            <span class="ds-lbl">{{ t('totalInvested') }}</span>
                        </div>
                        <p class="ds-val ds-btc-top">₿{{ formatBtcAmount(wallet.total_invested_btc ?? toBtc(displayInvested), 12) }}</p>
                        <div class="ds-usd-line">{{ currencySymbol }}{{ formatCurrency(displayInvested) }}</div>
                        <div class="ds-sub" style="color:#22d3ee">{{ activeInvestments.length }} {{ t('activePlans') }}</div>
                        <div class="ds-bar-track"><div class="ds-bar shimmer-bar" style="width:58%;background:linear-gradient(90deg,#0891b2,#22d3ee,#67e8f9)"></div></div>
                    </div>

                    <!-- Earnings -->
                    <div class="d-stat anim-up" style="--d:120ms;--ac:16,185,129">
                        <div class="ds-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(16,185,129,0.15),transparent 70%)"></div>
                        <div class="ds-top">
                            <div class="ds-icon" style="background:rgba(16,185,129,0.12);box-shadow:0 0 12px rgba(16,185,129,0.18)">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2" stroke-linecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                            </div>
                            <span class="ds-lbl">{{ t('totalEarnings') }}</span>
                        </div>
                        <p class="ds-val ds-btc-top">₿{{ formatBtcAmount(displayEarnings, 12) }}</p>
                        <div class="ds-usd-line">≈ {{ currencySymbol }}{{ formatCurrency(wallet.total_earnings_usd ?? 0) }}</div>
                        <div class="ds-sub" style="color:#10b981">{{ calculateROI() }}% {{ t('roi') }}</div>
                        <div class="ds-bar-track"><div class="ds-bar shimmer-bar" style="width:45%;background:linear-gradient(90deg,#059669,#10b981,#34d399)"></div></div>
                    </div>

                    <!-- Referral -->
                    <div class="d-stat anim-up" style="--d:160ms;--ac:232,121,249">
                        <div class="ds-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(232,121,249,0.15),transparent 70%)"></div>
                        <div class="ds-top">
                            <div class="ds-icon" style="background:rgba(232,121,249,0.12);box-shadow:0 0 12px rgba(232,121,249,0.18)">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#e879f9" stroke-width="2" stroke-linecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                            </div>
                            <span class="ds-lbl">{{ t('referralIncome') }}</span>
                        </div>
                        <p class="ds-val">{{ currencySymbol }}{{ formatCurrency(displayReferral) }}</p>
                        <div class="ds-sub" style="color:#e879f9">{{ user.total_referrals }} {{ t('referrals') }}</div>
                        <div class="ds-bar-track"><div class="ds-bar shimmer-bar" style="width:33%;background:linear-gradient(90deg,#a21caf,#e879f9,#f0abfc)"></div></div>
                    </div>
                </div>

                <!-- ── PROFIT PROGRESS + PORTFOLIO PERFORMANCE ROW ── -->
                <div class="d-pp-row anim-up" style="--d:200ms">

                    <!-- LEFT: Total Profit Progress (compact) -->
                    <div class="d-card d-pp-tpp-card" style="padding:0;overflow:hidden">
                        <TotalProfitProgress :progress-data="totalProfitProgress" :compact="true" />
                    </div>

                    <!-- RIGHT: Portfolio Performance + Distribution -->
                    <div class="d-card d-pp-right-card">

                        <!-- Portfolio Chart -->
                        <div class="d-pp-section">
                            <div class="d-card-head">
                                <div class="d-pp-title-wrap">
                                    <div class="d-pp-icon">
                                        <svg width="13" height="13" fill="none" stroke="#a75ded" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941"/></svg>
                                    </div>
                                    <div>
                                        <h3 class="d-title">{{ t('portfolioPerformance') }}</h3>
                                        <p class="d-sub">{{ t('realTimeTracking') }}</p>
                                    </div>
                                </div>
                                <div class="d-tf-group">
                                    <button v-for="tf in ['7D','30D','90D']" :key="tf"
                                        @click="changeTimeframe(tf)"
                                        class="d-tf"
                                        :class="selectedTimeframe===tf ? 'd-tf--on' : ''">{{ tf }}</button>
                                </div>
                            </div>
                            <div class="d-chart-h-sm">
                                <canvas ref="portfolioChart"></canvas>
                            </div>
                        </div>

                        <!-- Gradient divider -->
                        <div class="d-pp-divider"></div>

                        <!-- Distribution -->
                        <div class="d-pp-section d-pp-dist-section">
                            <div class="d-card-head" style="margin-bottom:10px">
                                <div class="d-pp-title-wrap">
                                    <div class="d-pp-icon" style="background:rgba(232,121,249,0.12);border-color:rgba(232,121,249,0.22)">
                                        <svg width="13" height="13" fill="none" stroke="#e879f9" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"/></svg>
                                    </div>
                                    <div>
                                        <h3 class="d-title" style="font-size:12px">{{ t('investmentDistribution') }}</h3>
                                        <p class="d-sub">{{ t('assetAllocation') }}</p>
                                    </div>
                                </div>
                            </div>
                            <div v-if="investmentDistribution.length" class="d-pp-dist-wrap">
                                <div class="d-pie-h-sm">
                                    <canvas ref="pieChart"></canvas>
                                </div>
                                <div class="d-dist-list-mini">
                                    <div v-for="item in investmentDistribution" :key="item.name" class="d-dist-row">
                                        <div class="flex items-center gap-2">
                                            <span class="d-dist-dot" :style="`background:${item.color}`"></span>
                                            <span class="text-[10px] text-slate-300 truncate">{{ item.name }}</span>
                                        </div>
                                        <span class="text-[10px] font-bold text-white">{{ item.value }}%</span>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="d-empty-sm" style="height:80px">
                                <div class="d-empty-ic" style="width:36px;height:36px"><svg width="18" height="18" style="color:#a75ded" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"/></svg></div>
                                <p class="text-xs text-slate-500">{{ t('noDistributionData') }}</p>
                            </div>
                        </div>

                    </div>
                </div>

                <!-- ── INVESTMENTS + ACTIONS ── -->
                <div class="d-grid-7-5 anim-up" style="--d:280ms">

                    <!-- Active Investments -->
                    <div class="d-card">
                        <div class="d-card-head mb-3">
                            <div>
                                <h3 class="d-title">{{ t('activeInvestmentPlans') }}</h3>
                                <p class="d-sub">{{ t('monitorInvestments') }}</p>
                            </div>
                            <Link href="/user/investments" class="d-link">{{ t('viewAllPlans') }} <svg width="10" height="10" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg></Link>
                        </div>

                        <!-- Table header -->
                        <div v-if="activeInvestments.length" class="d-inv-header">
                            <span>Plan</span>
                            <span>Amount</span>
                            <span>Rate</span>
                            <span>Progress</span>
                            <span>Earned</span>
                        </div>

                        <div v-if="activeInvestments.length" class="d-inv-list">
                            <div v-for="(inv, idx) in activeInvestments" :key="inv.id" class="d-inv-row">
                                <div class="d-inv-name">
                                    <span class="d-inv-num">#{{ idx+1 }}</span>
                                    <span class="d-inv-plan truncate">{{ inv.plan_name }}</span>
                                </div>
                                <span class="d-inv-amount">{{ formatInvestmentAmount(inv.amount, inv.btc_amount) }}</span>
                                <span class="d-inv-rate">{{ inv.interest_rate }}%</span>
                                <div class="d-inv-prog-wrap">
                                    <div class="d-inv-prog-bar">
                                        <div class="d-inv-prog-fill shimmer-bar"
                                             :style="`width:${Math.round((inv.returns_paid/inv.total_returns_expected)*100)}%;background:linear-gradient(90deg,#7c3aed,#a75ded,#c084fc)`"></div>
                                    </div>
                                    <span class="d-inv-prog-pct">{{ Math.round((inv.returns_paid/inv.total_returns_expected)*100) }}%</span>
                                </div>
                                <span class="d-inv-earned">{{ formatInvestmentAmount(inv.earned_amount, inv.btc_earned_amount_raw ?? inv.btc_earned_amount) }}</span>
                            </div>
                        </div>

                        <div v-else class="d-empty-lg">
                            <div class="d-empty-ic">
                                <svg width="26" height="26" style="color:#a75ded" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
                            </div>
                            <p class="text-sm text-slate-500">{{ t('noActiveInvestments') }}</p>
                            <Link href="/user/investments" class="d-cta">{{ t('newInvestment') }}</Link>
                        </div>
                    </div>

                    <!-- Right column: Quick Actions + AI -->
                    <div class="flex flex-col gap-4">

                        <!-- Quick Actions -->
                        <div class="d-card">
                            <h3 class="d-title mb-3">{{ t('quickActions') }}</h3>
                            <div class="d-qa-grid">
                                <Link href="/user/finance" class="d-qa">
                                    <div class="d-qa-ic" style="background:rgba(167,93,237,0.14);box-shadow:0 0 10px rgba(167,93,237,0.15)">
                                        <svg width="16" height="16" fill="none" stroke="#a75ded" stroke-width="2" stroke-linecap="round" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                    </div>
                                    <span class="d-qa-lbl">{{ t('makeDeposit') }}</span>
                                </Link>
                                <Link href="/user/investments" class="d-qa">
                                    <div class="d-qa-ic" style="background:rgba(34,211,238,0.12);box-shadow:0 0 10px rgba(34,211,238,0.12)">
                                        <svg width="16" height="16" fill="none" stroke="#22d3ee" stroke-width="2" stroke-linecap="round" viewBox="0 0 24 24"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
                                    </div>
                                    <span class="d-qa-lbl">{{ t('newInvestment') }}</span>
                                </Link>
                                <Link href="/user/referral-level" class="d-qa">
                                    <div class="d-qa-ic" style="background:rgba(232,121,249,0.12);box-shadow:0 0 10px rgba(232,121,249,0.12)">
                                        <svg width="16" height="16" fill="none" stroke="#e879f9" stroke-width="2" stroke-linecap="round" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                                    </div>
                                    <span class="d-qa-lbl">{{ t('referralLevel') }}</span>
                                </Link>
                                <Link href="/user/earnings" class="d-qa">
                                    <div class="d-qa-ic" style="background:rgba(16,185,129,0.12);box-shadow:0 0 10px rgba(16,185,129,0.12)">
                                        <svg width="16" height="16" fill="none" stroke="#10b981" stroke-width="2" stroke-linecap="round" viewBox="0 0 24 24"><path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>
                                    </div>
                                    <span class="d-qa-lbl">{{ t('viewAllHistory') }}</span>
                                </Link>
                            </div>
                        </div>

                        <!-- AI Insights -->
                        <div class="d-ai-card flex-1">
                            <div class="d-ai-glow"></div>
                            <div class="relative z-10">
                                <div class="d-card-head mb-3">
                                    <div class="flex items-center gap-2">
                                        <div class="d-ai-ic">
                                            <svg width="14" height="14" style="color:#a75ded" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
                                        </div>
                                        <span class="d-title">{{ t('aiInsights') }}</span>
                                    </div>
                                    <Link href="/user/ai-recommendations" class="text-[10px] font-bold" style="color:#a75ded">{{ t('viewAll') }} →</Link>
                                </div>
                                <div class="space-y-2">
                                    <div v-for="rec in aiRecommendations.slice(0,3)" :key="rec.id" class="d-ai-rec">
                                        <div class="flex items-start justify-between gap-2 mb-1">
                                            <p class="text-xs font-bold text-white flex-1 leading-snug">{{ rec.title }}</p>
                                            <span class="d-conf-badge">{{ rec.confidence_score }}%</span>
                                        </div>
                                        <p class="text-[10px] text-slate-400 leading-relaxed line-clamp-2 mb-1">{{ rec.description }}</p>
                                        <Link :href="`/user/ai-recommendations/${rec.id}`" class="text-[10px] font-bold" style="color:#a75ded">{{ t('learnMore') }} →</Link>
                                    </div>
                                    <div v-if="!aiRecommendations.length" class="text-center py-4">
                                        <p class="text-xs text-slate-600">No AI insights yet</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- ── TRANSACTIONS + REFERRAL ── -->
                <div class="d-grid-2 anim-up" style="--d:320ms">

                    <!-- Transactions -->
                    <div class="d-card">
                        <div class="d-card-head mb-3">
                            <div>
                                <h3 class="d-title">{{ t('recentTransactions') }}</h3>
                                <p class="d-sub">{{ t('latestActivity') }}</p>
                            </div>
                            <Link href="/user/earnings" class="d-link">{{ t('viewAllHistory') }} <svg width="10" height="10" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg></Link>
                        </div>

                        <!-- Table header -->
                        <div v-if="recentTransactions.length" class="d-tx-header">
                            <span>Type</span>
                            <span>Date</span>
                            <span style="text-align:right">Amount</span>
                        </div>

                        <div v-if="recentTransactions.length" class="d-tx-list">
                            <div v-for="tx in recentTransactions.slice(0,5)" :key="tx.id" class="d-tx-row">
                                <div class="d-tx-left">
                                    <div class="d-tx-dot"
                                         :style="tx.type==='deposit'||tx.type==='credit'
                                            ? 'background:rgba(16,185,129,0.15);box-shadow:0 0 6px rgba(16,185,129,0.3)'
                                            : 'background:rgba(167,93,237,0.15);box-shadow:0 0 6px rgba(167,93,237,0.2)'">
                                        <svg width="10" height="10"
                                             :style="tx.type==='deposit'||tx.type==='credit' ? 'color:#10b981' : 'color:#a75ded'"
                                             fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                                            <path v-if="tx.type==='deposit'||tx.type==='credit'" stroke-linecap="round" d="M12 5v14m7-7H5"/>
                                            <path v-else stroke-linecap="round" d="M5 12h14M12 5l7 7-7 7"/>
                                        </svg>
                                    </div>
                                    <span class="d-tx-type">{{ formatTransactionType(tx.type) }}</span>
                                </div>
                                <span class="d-tx-date">{{ formatDate(tx.created_at) }}</span>
                                <div class="d-tx-right">
                                    <span class="d-tx-amt"
                                          :style="tx.type==='deposit'||tx.type==='credit' ? 'color:#10b981' : 'color:#f43f5e'">
                                        {{ tx.type==='deposit'||tx.type==='credit' ? '+' : '-' }}{{ formatTxAmount(tx) }}
                                    </span>
                                    <span class="d-tx-badge"
                                          :class="tx.status==='completed'||tx.status==='success' ? 'd-s-done'
                                                : tx.status==='pending' ? 'd-s-pend' : 'd-s-fail'">{{ tx.status }}</span>
                                </div>
                            </div>
                        </div>

                        <div v-else class="d-empty-lg">
                            <div class="d-empty-ic"><svg width="24" height="24" style="color:#a75ded" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg></div>
                            <p class="text-sm text-slate-500">{{ t('noTransactionsYet') }}</p>
                        </div>
                    </div>

                    <!-- Referral Network -->
                    <div class="d-card">
                        <div class="d-card-head mb-3">
                            <div>
                                <h3 class="d-title">{{ t('referralNetwork') }}</h3>
                                <p class="d-sub">{{ t('networkPerformance') }}</p>
                            </div>
                            <Link href="/user/referral-network" class="d-link">{{ t('manageNetwork') }} <svg width="10" height="10" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg></Link>
                        </div>

                        <!-- Two mini stats -->
                        <div class="d-ref-stats">
                            <div class="d-ref-stat" style="border-color:rgba(167,93,237,0.2);background:rgba(167,93,237,0.05)">
                                <p class="d-ref-n" style="color:#a75ded">{{ referralStats.totalReferrals }}</p>
                                <p class="d-ref-l">{{ t('totalReferrals') }}</p>
                            </div>
                            <div class="d-ref-stat" style="border-color:rgba(232,121,249,0.2);background:rgba(232,121,249,0.05)">
                                <p class="d-ref-n" style="color:#e879f9">{{ referralStats.activeReferrals }}</p>
                                <p class="d-ref-l">{{ t('activeMembers') }}</p>
                            </div>
                            <div class="d-ref-stat" style="border-color:rgba(34,211,238,0.2);background:rgba(34,211,238,0.05)">
                                <p class="d-ref-n" style="color:#22d3ee">{{ currencySymbol }}{{ formatCurrency(referralStats.totalEarned) }}</p>
                                <p class="d-ref-l">{{ t('commissionEarned') }}</p>
                            </div>
                        </div>

                        <!-- Commission progress -->
                        <div class="mt-3">
                            <div class="flex justify-between text-[10px] text-slate-500 mb-1.5">
                                <span>{{ t('commissionEarned') }}</span>
                                <span class="font-bold text-white">{{ currencySymbol }}{{ formatCurrency(referralStats.totalEarned) }} / {{ currencySymbol }}20K</span>
                            </div>
                            <div class="d-prog-track">
                                <div class="d-prog-fill shimmer-bar"
                                     :style="`width:${Math.min((referralStats.totalEarned/20000)*100,100)}%;background:linear-gradient(90deg,#7c3aed,#a75ded,#e879f9)`"></div>
                            </div>
                        </div>

                        <!-- Referral code -->
                        <div class="d-ref-code">
                            <p class="text-[10px] font-semibold text-slate-500 mb-2">{{ t('yourReferralCode') }}</p>
                            <div class="flex items-center gap-2">
                                <code class="d-ref-code-val">{{ user.referral_code }}</code>
                                <button @click="copyReferralCode" class="d-copy-btn" title="Copy">
                                    <svg width="14" height="14" style="color:#a75ded" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

            </div><!-- /d-inner -->
        </div><!-- /d-wrap -->

        <LoginPopup :enabled="loginPopup.enabled" :title="loginPopup.title" :description="loginPopup.description" :image="loginPopup.image" />
    </UserLayout>
</template>

<script setup>
import {ref, computed, onMounted, onUnmounted} from 'vue';
import {usePage, Link} from '@inertiajs/vue3';
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue";
import TotalProfitProgress from "@/Components/User/Dashboard/TotalProfitProgress.vue";
import LoginPopup from "@/Components/User/LoginPopup.vue";
import {useToast} from "@/composables/useToast.js";
import {useTranslation} from '@/composables/useTranslation';
import {Chart, registerables} from 'chart.js';

Chart.register(...registerables);

const props = defineProps({
    wallet: { type: Object, required: true },
    user: { type: Object, required: true },
    activeInvestments: { type: Array, default: () => [] },
    recentTransactions: { type: Array, default: () => [] },
    aiRecommendations: { type: Array, default: () => [] },
    portfolioChartData: { type: Object, default: () => ({'7D':[],'30D':[],'90D':[]}) },
    investmentDistribution: { type: Array, default: () => [] },
    referralStats: {
        type: Object,
        default: () => ({totalReferrals:0,activeReferrals:0,totalEarned:0,pendingCommission:0,thisMonthEarnings:0,monthlyGrowth:0,teamInvestment:0})
    },
    totalProfitProgress: {
        type: Object,
        default: () => ({total_max_profit:0,total_roi_earned:0,total_commissions_earned:0,total_profit_earned:0,total_remaining:0,overall_progress:0,total_invested:0,active_investments:0,capped_investments:0,status:'no_investments',investments_breakdown:[]})
    },
    loginPopup: { type: Object, default: () => ({enabled:false,title:'Welcome Back!',description:'We are glad to see you again',image:null}) }
});

const {showToast} = useToast();
const {t} = useTranslation();
const page = usePage();

const portfolioChart = ref(null);
const pieChart = ref(null);
let portfolioChartInstance = null;
let pieChartInstance = null;
const selectedTimeframe = ref('7D');

const displayBalance  = ref(0);
const displayInvested = ref(0);
const displayEarnings = ref(0);
const displayReferral = ref(0);
const displayRioBalance = ref(0);

const primaryColor   = computed(() => page.props.primaryColor || '#a75ded');
const currencySymbol = computed(() => page.props.currencySymbol || '$');
const btcPriceUsd    = computed(() => parseFloat(page.props.btcPriceUsd || 0));
const userInitials   = computed(() => {
    const n = props.user?.name || '';
    return n.split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase() || 'U';
});

const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return t('goodMorning') || 'Good Morning';
    if (h < 17) return t('goodAfternoon') || 'Good Afternoon';
    return t('goodEvening') || 'Good Evening';
};

const hexToRgb = (hex) => {
    if (!hex) return {r:167,g:93,b:237};
    const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return r ? {r:parseInt(r[1],16),g:parseInt(r[2],16),b:parseInt(r[3],16)} : {r:167,g:93,b:237};
};
const rgbToHex = (r,g,b) => '#'+((1<<24)+(r<<16)+(g<<8)+b).toString(16).slice(1);
const adjustColor = (hex,amt) => {
    const {r,g,b} = hexToRgb(hex);
    return rgbToHex(Math.min(255,Math.max(0,r+amt)),Math.min(255,Math.max(0,g+amt)),Math.min(255,Math.max(0,b+amt)));
};
const themeColors = computed(() => {
    const {r,g,b} = hexToRgb(primaryColor.value);
    return {primary:primaryColor.value, primaryDark:adjustColor(primaryColor.value,-30), primaryRgb:`${r},${g},${b}`};
});

const toBtc = (amount) => {
    const usd = parseFloat(amount) || 0;
    const price = btcPriceUsd.value;
    return price > 0 ? usd / price : usd;
};

const countUp = (refObj, endVal, duration=1400) => {
    const end = parseFloat(endVal) || 0;
    if (end===0) { refObj.value=0; return; }
    const start = performance.now();
    const run = (now) => {
        const p = Math.min((now-start)/duration,1);
        refObj.value = end*(1-Math.pow(1-p,4));
        if (p<1) requestAnimationFrame(run); else refObj.value=end;
    };
    requestAnimationFrame(run);
};

const initPortfolioChart = () => {
    if (!portfolioChart.value) return;
    const ctx = portfolioChart.value.getContext('2d');
    if (!ctx) return;
    const gradient = ctx.createLinearGradient(0,0,0,300);
    gradient.addColorStop(0,`rgba(${themeColors.value.primaryRgb},0.35)`);
    gradient.addColorStop(0.6,`rgba(${themeColors.value.primaryRgb},0.08)`);
    gradient.addColorStop(1,`rgba(${themeColors.value.primaryRgb},0.0)`);
    const data = props.portfolioChartData[selectedTimeframe.value] || [];
    portfolioChartInstance = new Chart(ctx, {
        type: 'line',
        data: {
            labels: data.map(i=>i.date),
            datasets: [{
                label: t('portfolioValue'),
                data: data.map(i=>i.value),
                borderColor: themeColors.value.primary,
                backgroundColor: gradient,
                borderWidth: 2,
                fill: true,
                tension: 0.4,
                pointBackgroundColor: themeColors.value.primary,
                pointBorderColor: '#07030f',
                pointBorderWidth: 2,
                pointRadius: 3,
                pointHoverRadius: 6
            }]
        },
        options: {
            responsive: true, maintainAspectRatio: false,
            plugins: {
                legend: {display:false},
                tooltip: {
                    backgroundColor: 'rgba(7,3,15,0.97)',
                    borderColor: `rgba(${themeColors.value.primaryRgb},0.3)`,
                    borderWidth: 1, titleColor:'#fff',
                    bodyColor: themeColors.value.primary,
                    cornerRadius: 10, displayColors: false, padding: 10,
                    callbacks: { label: c=>`${currencySymbol.value}${formatNumber(c.parsed.y)}` }
                }
            },
            scales: {
                x: { grid:{display:false}, ticks:{color:'#374151',font:{size:10}}, border:{display:false} },
                y: {
                    grid:{color:'rgba(167,93,237,0.06)',borderDash:[4,4]},
                    ticks:{color:'#374151',font:{size:10},callback:v=>`${currencySymbol.value}${(v/1000).toFixed(0)}K`},
                    border:{display:false}
                }
            },
            interaction:{intersect:false,mode:'index'}
        }
    });
};

const initPieChart = () => {
    if (!pieChart.value || !props.investmentDistribution.length) return;
    const ctx = pieChart.value.getContext('2d');
    if (!ctx) return;
    pieChartInstance = new Chart(ctx, {
        type: 'doughnut',
        data: {
            labels: props.investmentDistribution.map(i=>i.name),
            datasets: [{data:props.investmentDistribution.map(i=>i.value),backgroundColor:props.investmentDistribution.map(i=>i.color),borderWidth:0,cutout:'72%',hoverOffset:4}]
        },
        options: {
            responsive:true, maintainAspectRatio:false,
            plugins:{legend:{display:false},tooltip:{backgroundColor:'rgba(7,3,15,0.97)',borderColor:`rgba(${themeColors.value.primaryRgb},0.3)`,borderWidth:1,titleColor:'#fff',bodyColor:'#cbd5e1',cornerRadius:10}}
        }
    });
};

const updatePortfolioChart = () => {
    if (!portfolioChartInstance||!portfolioChart.value) return;
    const ctx = portfolioChart.value.getContext('2d');
    const data = props.portfolioChartData[selectedTimeframe.value] || [];
    const gradient = ctx.createLinearGradient(0,0,0,300);
    gradient.addColorStop(0,`rgba(${themeColors.value.primaryRgb},0.35)`);
    gradient.addColorStop(1,`rgba(${themeColors.value.primaryRgb},0.0)`);
    portfolioChartInstance.data.labels = data.map(i=>i.date);
    portfolioChartInstance.data.datasets[0].data = data.map(i=>i.value);
    portfolioChartInstance.data.datasets[0].backgroundColor = gradient;
    portfolioChartInstance.update('active');
};

const changeTimeframe = (tf) => { selectedTimeframe.value=tf; updatePortfolioChart(); };

const copyReferralCode = async () => {
    if (!props.user?.referral_code) { showToast(t('noReferralCode')||'No referral code','error'); return; }
    try {
        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(props.user.referral_code);
        else { const ta=document.createElement('textarea');ta.value=props.user.referral_code;ta.style.cssText='position:fixed;left:-9999px';document.body.appendChild(ta);ta.select();document.execCommand('copy');document.body.removeChild(ta); }
        showToast(t('referralCodeCopied')||'Copied!','success');
    } catch { showToast(t('failedToCopy')||'Failed','error'); }
};

const formatCurrency = (v) => new Intl.NumberFormat('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}).format(parseFloat(v)||0);
const formatBtcAmount = (v, precision = 12) => new Intl.NumberFormat('en-US',{minimumFractionDigits:precision,maximumFractionDigits:precision}).format(parseFloat(v)||0);
const formatNumber = (num) => {
    const n=parseFloat(num)||0;
    if(n>=1e9) return(n/1e9).toFixed(2)+'B';
    if(n>=1e6) return(n/1e6).toFixed(2)+'M';
    if(n>=1e3) return(n/1e3).toFixed(2)+'K';
    return Number.isInteger(n)?n.toString():n.toFixed(2);
};
const formatDate = (d) => {
    if(!d) return 'N/A';
    try { const dt=new Date(d); return isNaN(dt)?'—':dt.toLocaleDateString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}); } catch { return '—'; }
};
const formatTransactionType = (type) => {
    if(!type) return '';
    const m={deposit:t('deposit')||'Deposit',withdrawal:t('withdrawal')||'Withdrawal',credit:t('credit')||'Credit',debit:t('debit')||'Debit',transfer:t('transfer')||'Transfer',payment:t('payment')||'Payment',refund:t('refund')||'Refund'};
    return m[type.toLowerCase()]||type.charAt(0).toUpperCase()+type.slice(1);
};
const getRawBtcFromDetails = (details) => {
    if (!details || typeof details !== 'string') return null;
    const match = details.match(/Raw BTC:\s*([0-9.]+)/i);
    if (!match) return null;
    const value = parseFloat(match[1]);
    return Number.isNaN(value) ? null : value;
};
const isBtcTransaction = (tx) => tx?.currency === 'BTC' || tx?.wallet_type === 'rio';
const formatTxAmount = (tx) => isBtcTransaction(tx)
    ? (() => {
        const rawBtc = getRawBtcFromDetails(tx?.details);
        const precision = 12;
        return `₿${formatBtcAmount(rawBtc !== null ? rawBtc : tx.amount, precision)}`;
    })()
    : `${currencySymbol.value}${formatCurrency(tx.amount)}`;
const formatInvestmentAmount = (amount, btcAmount = null) => {
    if (btcAmount !== null && btcAmount !== undefined) {
        const precision = 12;
        return `₿${formatBtcAmount(btcAmount, precision)}`;
    }

    return btcPriceUsd.value > 0
        ? `₿${formatBtcAmount(toBtc(amount))}`
        : `${currencySymbol.value}${formatCurrency(amount)}`;
};
const calculateROI = () => {
    const earningsUsd = parseFloat(props.wallet?.total_earnings_usd) || 0;
    const invested = parseFloat(props.wallet?.total_invested) || 0;
    if (invested === 0) return 0;
    return Math.round((earningsUsd / invested) * 100);
};

onMounted(() => {
    setTimeout(() => { initPortfolioChart(); initPieChart(); }, 120);
    countUp(displayBalance,  props.wallet?.balance);
    countUp(displayRioBalance, props.wallet?.rio_balance);
    countUp(displayInvested, props.wallet?.total_invested, 1600);
    countUp(displayEarnings, props.wallet?.total_rio_earnings, 1800);
    countUp(displayReferral, props.wallet?.total_referral_bonus, 2000);
});

onUnmounted(() => {
    portfolioChartInstance?.destroy();
    pieChartInstance?.destroy();
    portfolioChartInstance = null;
    pieChartInstance = null;
});
</script>

<style scoped>
/* ════════ PAGE SHELL ════════ */
.d-wrap  { position:relative; z-index:10; width:100%; }
.d-inner { max-width:1280px; margin:0 auto; padding:16px 14px; display:flex; flex-direction:column; gap:14px; }
@media(min-width:640px)  { .d-inner{ padding:20px 20px; gap:16px; } }
@media(min-width:1024px) { .d-inner{ padding:20px 24px; gap:18px; } }

/* ════════ ENTRANCE ANIMATION ════════ */
.anim-up { animation:slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both; animation-delay:var(--d,0ms); }
@keyframes slideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }

/* ════════ BASE CARD ════════ */
.d-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.14);
    border-radius: 14px;
    padding: 14px;
    position: relative;
    overflow: hidden;
    transition: border-color 0.25s;
}
.d-card::before {
    content:'';
    position:absolute;
    top:0; left:15%; right:15%; height:1px;
    background:linear-gradient(90deg,transparent,rgba(167,93,237,0.35),transparent);
}
.d-card:hover { border-color:rgba(167,93,237,0.25); }
@media(min-width:640px) { .d-card{ padding:16px; border-radius:16px; } }

/* ════════ CARD HEADER ════════ */
.d-card-head { display:flex; align-items:center; justify-content:space-between; gap:10px; }
.d-title { font-size:13px; font-weight:800; color:#fff; letter-spacing:-0.01em; }
.d-sub   { font-size:10px; color:#374151; margin-top:1px; }
.d-link  {
    display:inline-flex; align-items:center; gap:4px;
    font-size:10px; font-weight:700; color:#a75ded;
    padding:3px 9px; border-radius:7px;
    border:1px solid rgba(167,93,237,0.28);
    background:rgba(167,93,237,0.07);
    white-space:nowrap; transition:all 0.2s;
}
.d-link:hover { background:rgba(167,93,237,0.14); }

/* ════════ LAYOUTS ════════ */
.d-grid-7-5 { display:grid; gap:14px; }
.d-grid-2   { display:grid; gap:14px; }
@media(min-width:768px)  {
    .d-grid-2  { grid-template-columns:1fr 1fr; }
}
@media(min-width:900px)  {
    .d-grid-7-5 { grid-template-columns:1fr 340px; }
}
@media(min-width:1024px) {
    .d-grid-7-5 { grid-template-columns:1fr 300px; }
}

/* ════════ PROFIT PROGRESS + PORTFOLIO PERFORMANCE ROW ════════ */
.d-pp-row { display:grid; gap:14px; }
@media(min-width:960px) {
    .d-pp-row { grid-template-columns:55fr 45fr; align-items:stretch; }
}

.d-pp-right-card {
    padding: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.d-pp-section {
    padding: 14px;
    flex-shrink: 0;
}
@media(min-width:640px) { .d-pp-section { padding: 16px; } }

.d-pp-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(167,93,237,0.18), rgba(232,121,249,0.12), transparent);
    margin: 0;
    flex-shrink: 0;
}

.d-pp-dist-section { flex: 1; }

.d-chart-h-sm { height: 160px; }
@media(min-width:640px) { .d-chart-h-sm { height: 180px; } }
@media(min-width:960px) { .d-chart-h-sm { height: 170px; } }

.d-pp-title-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
}
.d-pp-icon {
    width: 28px; height: 28px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: rgba(167,93,237,0.12);
    border: 1px solid rgba(167,93,237,0.22);
}

.d-pp-dist-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
}
.d-pie-h-sm {
    width: 90px; height: 90px;
    flex-shrink: 0;
}
.d-dist-list-mini {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
    max-height: 90px;
    overflow-y: auto;
}

/* ════════ WELCOME STRIP ════════ */
.d-welcome {
    display:flex; align-items:center; justify-content:space-between;
    gap:12px; flex-wrap:wrap;
    padding:12px 16px; border-radius:12px;
    background:rgba(167,93,237,0.06);
    border:1px solid rgba(167,93,237,0.16);
}
.dw-left { display:flex; align-items:center; gap:10px; flex-shrink:0; }
.dw-av {
    width:38px; height:38px; border-radius:50%;
    background:linear-gradient(135deg,#7c3aed,#a75ded);
    display:flex; align-items:center; justify-content:center;
    font-size:13px; font-weight:800; color:#fff;
    box-shadow:0 0 14px rgba(167,93,237,0.4); flex-shrink:0;
}
.dw-greet { font-size:9px; font-weight:600; color:rgba(167,93,237,0.7); text-transform:uppercase; letter-spacing:0.1em; }
.dw-name  { font-size:14px; font-weight:800; color:#fff; letter-spacing:-0.02em; }
.dw-email {
    font-size:10px; color:rgba(255,255,255,0.38); font-family:'Courier New',monospace;
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:200px;
    margin-top:1px;
}
.dw-uid-row {
    display:inline-flex; align-items:center; gap:4px; margin-top:3px;
}
.dw-uid-lbl {
    font-size:8px; font-weight:800; text-transform:uppercase; letter-spacing:0.1em;
    color:rgba(167,93,237,0.65); background:rgba(167,93,237,0.1);
    padding:1px 5px; border-radius:4px; border:1px solid rgba(167,93,237,0.22);
}
.dw-uid-val {
    font-size:11px; font-weight:700; color:#c084fc;
    font-family:'Courier New',monospace; letter-spacing:0.04em;
}
.dw-chips { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.dw-chip  {
    display:flex; flex-direction:column; align-items:center; gap:1px;
    padding:6px 12px; border-radius:9px;
    background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07);
    min-width:56px;
}
.dw-chip-l { font-size:8px; font-weight:700; color:#4b5563; text-transform:uppercase; letter-spacing:0.12em; }
.dw-chip-v { font-size:13px; font-weight:900; color:#fff; }
.dw-bal {
    display:flex; flex-direction:column; gap:2px;
    padding:6px 12px; border-radius:9px;
    background:rgba(167,93,237,0.1); border:1px solid rgba(167,93,237,0.22);
}
.dw-bal-v { font-size:14px; font-weight:900; color:#fff; letter-spacing:-0.02em; }
.d-trend-up { font-size:9px; font-weight:800; padding:1px 6px; border-radius:999px; background:rgba(16,185,129,0.15); color:#10b981; }
.d-trend-dn { font-size:9px; font-weight:800; padding:1px 6px; border-radius:999px; background:rgba(244,63,94,0.15);  color:#f43f5e; }

/* ════════ STAT CARDS ════════ */
.d-stat-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px;
}
@media(min-width:768px) { .d-stat-row{ gap:12px; } }

.d-stat {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.12);
    border-radius: 13px;
    padding: 12px 14px;
    position: relative; overflow: hidden;
    transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s;
    cursor: default;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 116px; /* uniform card height */
}
.d-stat:hover { transform:translateY(-3px); box-shadow:0 10px 32px rgba(0,0,0,0.4),0 0 20px rgba(var(--ac),0.1); }
.ds-glow { position:absolute; inset:0; pointer-events:none; }
.ds-top  { display:flex; align-items:center; gap:8px; margin-bottom:10px; position:relative; z-index:1; }
.ds-icon {
    width:30px; height:30px; border-radius:9px;
    display:flex; align-items:center; justify-content:center; flex-shrink:0;
    transition:transform 0.25s;
}
.d-stat:hover .ds-icon { transform:scale(1.1); }
.ds-lbl  { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.12em; color:#374151; }
.ds-val  { font-size:1.05rem; font-weight:900; color:#fff; letter-spacing:-0.02em; line-height:1.1; position:relative; z-index:1; margin-bottom:3px; }
@media(min-width:768px) { .ds-val { font-size:1.15rem; } }
@media(min-width:1024px) { .ds-val{ font-size:1.2rem; } }
/* keep small modifier for compact displays */
.ds-val--small { font-size: inherit; }
.ds-btc-top { color: #FF6F00; margin-bottom: 2px; }
.ds-usd-line {
    font-size: 0.86rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.82);
    letter-spacing: -0.01em;
    margin-bottom: 6px;
}
@media(min-width:768px) { .ds-usd-line { font-size: 0.92rem; } }
.ds-sub  { font-size:10px; font-weight:700; position:relative; z-index:1; margin-bottom:10px; }
.ds-bar-track { height:2px; background:rgba(255,255,255,0.06); border-radius:999px; overflow:hidden; position:relative; z-index:1; }
.ds-bar { height:100%; border-radius:999px; }

/* ════════ SHIMMER ════════ */
.shimmer-bar { position:relative; overflow:hidden; }
.shimmer-bar::after {
    content:''; position:absolute; top:0; left:-150%; width:80%; height:100%;
    background:linear-gradient(90deg,transparent,rgba(255,255,255,0.3),transparent);
    animation:shimmerRun 2.6s ease-in-out infinite;
}
@keyframes shimmerRun { 0%{left:-150%} 100%{left:200%} }

/* ════════ DISTRIBUTION ════════ */
.d-dist-row { display:flex; align-items:center; justify-content:space-between; padding:4px 7px; border-radius:6px; background:rgba(255,255,255,0.02); border:1px solid rgba(255,255,255,0.04); transition:background 0.2s; }
.d-dist-row:hover { background:rgba(167,93,237,0.05); }
.d-dist-dot { width:6px; height:6px; border-radius:50%; flex-shrink:0; }

/* ════════ ACTIVE INVESTMENTS - TABLE STYLE ════════ */
.d-inv-header {
    display:grid;
    grid-template-columns:2fr 100px 60px 1fr 90px;
    gap:8px;
    padding:0 10px 6px;
    font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.1em;
    color:#374151; border-bottom:1px solid rgba(255,255,255,0.06);
    margin-bottom:4px;
}
@media(max-width:639px) { .d-inv-header { display:none; } }

.d-inv-list { display:flex; flex-direction:column; gap:2px; }

.d-inv-row {
    display:grid;
    grid-template-columns:2fr 100px 60px 1fr 90px;
    gap:8px;
    align-items:center;
    padding:8px 10px;
    border-radius:9px;
    border:1px solid transparent;
    background:rgba(255,255,255,0.015);
    transition:all 0.2s;
}
.d-inv-row:hover { background:rgba(167,93,237,0.06); border-color:rgba(167,93,237,0.18); }

/* Mobile: stack to cards */
@media(max-width:639px) {
    .d-inv-row {
        grid-template-columns:1fr 1fr;
        grid-template-rows:auto auto auto;
        gap:6px;
        padding:10px;
    }
    .d-inv-name  { grid-column:1/2; }
    .d-inv-amount{ grid-column:2/3; text-align:right; }
    .d-inv-rate  { display:none; }
    .d-inv-prog-wrap { grid-column:1/3; }
    .d-inv-earned{ grid-column:1/3; color:#a75ded; font-size:10px; }
}

.d-inv-name { display:flex; align-items:center; gap:6px; min-width:0; }
.d-inv-num {
    font-size:9px; font-weight:800; padding:2px 5px; border-radius:5px;
    background:rgba(167,93,237,0.18); color:#a75ded; flex-shrink:0;
}
.d-inv-plan { font-size:12px; font-weight:700; color:#fff; truncate:true; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.d-inv-amount{ font-size:12px; font-weight:800; color:#fff; font-variant-numeric:tabular-nums; }
.d-inv-rate  { font-size:11px; font-weight:700; color:#a75ded; }
.d-inv-prog-wrap { display:flex; align-items:center; gap:6px; }
.d-inv-prog-bar  { flex:1; height:4px; background:rgba(255,255,255,0.07); border-radius:999px; overflow:hidden; }
.d-inv-prog-fill { height:100%; border-radius:999px; transition:width 1s cubic-bezier(0.34,1.56,0.64,1); }
.d-inv-prog-pct  { font-size:10px; font-weight:700; color:#fff; width:28px; text-align:right; flex-shrink:0; }
.d-inv-earned { font-size:11px; font-weight:800; color:#a75ded; font-variant-numeric:tabular-nums; }

/* ════════ QUICK ACTIONS - 2x2 GRID ════════ */
.d-qa-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
.d-qa {
    display:flex; flex-direction:column; align-items:center; gap:7px;
    padding:12px 10px; border-radius:11px;
    border:1px solid rgba(255,255,255,0.06);
    background:rgba(255,255,255,0.02);
    text-decoration:none;
    transition:all 0.22s;
}
.d-qa:hover { background:rgba(167,93,237,0.08); border-color:rgba(167,93,237,0.25); transform:translateY(-2px); }
.d-qa-ic {
    width:36px; height:36px; border-radius:10px;
    display:flex; align-items:center; justify-content:center; flex-shrink:0;
    transition:transform 0.22s;
}
.d-qa:hover .d-qa-ic { transform:scale(1.1); }
.d-qa-lbl { font-size:10px; font-weight:700; color:#94a3b8; text-align:center; line-height:1.2; }

/* ════════ AI CARD ════════ */
.d-ai-card {
    background:rgba(10,4,22,0.97);
    border:1px solid rgba(167,93,237,0.3);
    border-radius:14px;
    padding:14px;
    position:relative; overflow:hidden;
    box-shadow:0 0 24px rgba(167,93,237,0.05);
}
.d-ai-glow {
    position:absolute; width:160px; height:160px;
    top:-60px; right:-50px; border-radius:50%;
    background:radial-gradient(circle,rgba(167,93,237,0.12) 0%,transparent 70%);
    filter:blur(16px); pointer-events:none;
}
.d-ai-ic {
    width:26px; height:26px; border-radius:7px;
    border:1px solid rgba(167,93,237,0.35);
    background:rgba(167,93,237,0.14);
    box-shadow:0 0 10px rgba(167,93,237,0.25);
    display:flex; align-items:center; justify-content:center;
    animation:aiPls 3s ease-in-out infinite;
}
@keyframes aiPls { 0%,100%{box-shadow:0 0 10px rgba(167,93,237,0.25)} 50%{box-shadow:0 0 18px rgba(167,93,237,0.5)} }
.d-ai-rec {
    padding:8px 10px; border-radius:8px;
    border:1px solid rgba(167,93,237,0.12);
    background:rgba(167,93,237,0.04);
    transition:all 0.2s;
}
.d-ai-rec:hover { background:rgba(167,93,237,0.08); border-color:rgba(167,93,237,0.25); }
.d-conf-badge {
    font-size:8px; font-weight:800;
    padding:2px 5px; border-radius:5px;
    background:rgba(167,93,237,0.18); color:#a75ded; flex-shrink:0;
}

/* ════════ TRANSACTIONS - TABLE STYLE ════════ */
.d-tx-header {
    display:grid; grid-template-columns:1.5fr 1fr auto;
    gap:8px; padding:0 8px 5px;
    font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.1em;
    color:#374151; border-bottom:1px solid rgba(255,255,255,0.06); margin-bottom:2px;
}
.d-tx-list { display:flex; flex-direction:column; }
.d-tx-row {
    display:grid; grid-template-columns:1.5fr 1fr auto;
    gap:8px; align-items:center;
    padding:7px 8px;
    border-radius:8px;
    border:1px solid transparent;
    transition:all 0.18s;
}
.d-tx-row:hover { background:rgba(167,93,237,0.04); border-color:rgba(167,93,237,0.14); }
.d-tx-left { display:flex; align-items:center; gap:8px; min-width:0; }
.d-tx-dot  {
    width:24px; height:24px; border-radius:7px; flex-shrink:0;
    display:flex; align-items:center; justify-content:center;
}
.d-tx-type { font-size:12px; font-weight:700; color:#fff; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.d-tx-date { font-size:10px; color:#374151; }
.d-tx-right{ display:flex; flex-direction:column; align-items:flex-end; gap:2px; flex-shrink:0; }
.d-tx-amt  { font-size:12px; font-weight:800; font-variant-numeric:tabular-nums; }
.d-tx-badge { font-size:8px; font-weight:700; padding:1px 5px; border-radius:5px; text-transform:capitalize; }
.d-s-done { background:rgba(16,185,129,0.15);  color:#10b981; }
.d-s-pend { background:rgba(245,158,11,0.15);  color:#f59e0b; }
.d-s-fail { background:rgba(244,63,94,0.15);   color:#f43f5e; }

/* ════════ REFERRAL NETWORK ════════ */
.d-ref-stats { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; }
.d-ref-stat  { padding:10px 12px; border-radius:10px; border:1px solid; text-align:center; transition:transform 0.22s; }
.d-ref-stat:hover { transform:scale(1.02); }
.d-ref-n { font-size:1.1rem; font-weight:900; color:#fff; letter-spacing:-0.02em; font-variant-numeric:tabular-nums; line-height:1.1; word-break:break-all; }
.d-ref-l { font-size:9px; font-weight:700; margin-top:3px; color:#374151; text-transform:uppercase; letter-spacing:0.08em; }
.d-prog-track { height:4px; background:rgba(255,255,255,0.06); border-radius:999px; overflow:hidden; }
.d-prog-fill  { height:100%; border-radius:999px; transition:width 1s cubic-bezier(0.34,1.56,0.64,1); }
.d-ref-code   { padding:10px 12px; border-radius:10px; border:1px solid rgba(167,93,237,0.18); background:rgba(167,93,237,0.04); margin-top:12px; }
.d-ref-code-val {
    flex:1; padding:7px 12px; border-radius:8px;
    font-size:12px; font-family:monospace; font-weight:700; color:#a75ded;
    background:rgba(0,0,0,0.5); border:1px solid rgba(167,93,237,0.2);
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}
.d-copy-btn {
    padding:8px; border-radius:8px;
    border:1px solid rgba(167,93,237,0.3);
    background:rgba(167,93,237,0.1);
    cursor:pointer; transition:all 0.2s; flex-shrink:0;
    display:flex; align-items:center; justify-content:center;
}
.d-copy-btn:hover { background:rgba(167,93,237,0.2); transform:scale(1.08); }

/* ════════ EMPTY STATES ════════ */
.d-empty-sm { height:130px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; }
.d-empty-lg { padding:32px 16px; display:flex; flex-direction:column; align-items:center; gap:10px; }
.d-empty-ic { width:48px; height:48px; border-radius:12px; border:1px solid rgba(167,93,237,0.18); background:rgba(167,93,237,0.06); display:flex; align-items:center; justify-content:center; }
.d-cta {
    display:inline-flex; align-items:center; font-size:11px; font-weight:700; color:#fff;
    padding:7px 16px; border-radius:8px;
    background:linear-gradient(135deg,#7c3aed,#a75ded);
    box-shadow:0 4px 14px rgba(167,93,237,0.35); transition:all 0.22s;
}
.d-cta:hover { transform:translateY(-2px); box-shadow:0 8px 22px rgba(167,93,237,0.45); }

/* ════════ TF BUTTONS ════════ */
.d-tf-group { display:flex; align-items:center; gap:4px; }
.d-tf {
    padding:4px 11px; font-size:10px; font-weight:700; color:#374151;
    background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07);
    border-radius:7px; cursor:pointer; transition:all 0.2s; outline:none;
}
.d-tf:hover:not(.d-tf--on) { color:#94a3b8; background:rgba(255,255,255,0.07); }
.d-tf--on { color:#a75ded; background:rgba(167,93,237,0.15); border-color:rgba(167,93,237,0.4); box-shadow:0 0 10px rgba(167,93,237,0.18); }

/* ════════ SCROLLBAR ════════ */
::-webkit-scrollbar { width:4px; height:4px; }
::-webkit-scrollbar-track { background:transparent; }
::-webkit-scrollbar-thumb { background:rgba(167,93,237,0.28); border-radius:4px; }
::-webkit-scrollbar-thumb:hover { background:rgba(167,93,237,0.5); }

/* ════════ FOCUS ════════ */
:focus-visible { outline:2px solid rgba(167,93,237,0.6); outline-offset:2px; border-radius:5px; }

/* ════════ REDUCED MOTION ════════ */
@media(prefers-reduced-motion:reduce) {
    .anim-up,.d-ai-ic { animation:none!important; }
    .anim-up { opacity:1; transform:none; }
    .shimmer-bar::after { animation:none!important; }
}
</style>
